import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

import { Loader, Button } from '@treezor/design-system';

import { InfoTooltip } from '@/ui-kit/Tips/InfoTooltip';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { useProfileContext } from '@/features/context/ProfileContext';
import { IPaymentCard, TCardBlockType } from '@/features/cards/store/models/card.model';

import { navigateWalletFromCard } from '@/store/actions/profile.actions';
import { openModal } from '@/store/actions/core.actions';

import { ActionMenu } from '@/ui-kit/action-menu/ActionMenu';
import { TMenuAction } from '@/ui-kit/action-menu/ActionMenu.types';

import { IPaymentCardSettingsProps } from './CardSettings';

export const CardActions = ({ paymentCard }: { paymentCard: IPaymentCard }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { user } = useProfileContext();

  const isNavigationLoading = useAppSelector((state) => state.navigation.isLoading);

  const paymentCardStatus = paymentCard && paymentCard.isLive ? paymentCard.statusCode : 'INACTIVE';

  const isBlocked =
    paymentCardStatus === 'STOLEN' ||
    paymentCardStatus === 'LOST' ||
    paymentCardStatus === 'DESTROYED' ||
    paymentCardStatus === 'LOCK_INTERNAL';

  const togglePaymentCardSettingsDialog = (menu: TCardBlockType) => {
    dispatch(
      openModal<IPaymentCardSettingsProps>(
        'paymentCardSettingsModal',
        { activeMenu: menu, user },
        { fullWidth: true, maxWidth: 'lg' },
      ),
    );
  };

  const menuButtons: TMenuAction[] = [
    {
      name: 'settings',
      icon: 'settings',
      text: t('pages.currentProfiles.cards.settings.settings'),
      onClick: () => togglePaymentCardSettingsDialog('options'),
    },
    {
      name: 'walletRedirect',
      icon: isNavigationLoading ? undefined : 'account_balance_wallet',
      prefix: isNavigationLoading ? <Loader /> : undefined,
      text: t('pages.currentProfiles.cards.settings.walletRedirect'),
      onClick: () => dispatch(navigateWalletFromCard(paymentCard?.walletId, navigate)),
      shouldKeepOpen: true,
    },
  ];

  return (
    <div
      className={clsx(
        'flex items-center mx-6',
        (isBlocked || !Boolean(paymentCard?.isLive)) && paymentCardStatus !== 'LOCK_INTERNAL'
          ? 'justify-end'
          : 'justify-between',
      )}
    >
      {paymentCardStatus === 'LOCK_INTERNAL' && (
        <>
          <span className="text-tertiary">{t('pages.currentProfiles.cards.card.internalBlockCard.title')}</span>
          <InfoTooltip
            tooltipInfo={t('pages.currentProfiles.cards.card.internalBlockCard.tooltip')}
            infoClassName="w-[180px]"
          />
        </>
      )}
      {!isBlocked && !!paymentCard?.isLive ? (
        <Button
          icon="block"
          variant="ghost"
          color="danger"
          onClick={() => togglePaymentCardSettingsDialog('block')}
          data-testid="block-card-btn"
        >
          {t('pages.currentProfiles.cards.settings.blockCard.title')}
        </Button>
      ) : null}
      <ActionMenu actions={menuButtons} />
    </div>
  );
};
